import { Box, Typography } from '@mui/material';
import { ReactNode, memo } from 'react';

import { CatalogItemImage } from 'shared/ui/CatalogItemImage';
import { useImageLightbox } from 'shared/ui/ImageLightbox';
import { ProductRowTitleBlock } from 'shared/ui/ProductRowTitleBlock';

export interface CatalogItemProps {
    uom: string;
    brand: string;
    qtyPerUom: number;
    title: string;
    itemLabel: string;
    thumbnailUrl: string;
    isAvailable: boolean;
    isAddedToCatalog?: boolean;
    leadingAction?: ReactNode;
    action: ReactNode;
    supplierSlot: ReactNode;
    priceSlot: ReactNode;
    onClickTitle: () => void;
}

export const CatalogItem = memo(
    ({
        uom,
        brand,
        qtyPerUom,
        title,
        itemLabel,
        thumbnailUrl,
        isAvailable,
        isAddedToCatalog = false,
        leadingAction,
        supplierSlot,
        priceSlot,
        action,
        onClickTitle,
    }: CatalogItemProps) => {
        const { openLightbox } = useImageLightbox();

        return (
            <Box
                sx={{
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    position: 'relative',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'flex-start',
                        gap: '16px',
                    }}
                >
                    {leadingAction ? (
                        <Box
                            sx={{
                                flexShrink: 0,
                                paddingTop: '4px',
                            }}
                        >
                            {leadingAction}
                        </Box>
                    ) : null}
                    <Box
                        onClick={() => openLightbox(thumbnailUrl)}
                        sx={{
                            cursor: 'pointer',
                            position: 'relative',
                            width: '100px',
                            height: '100px',
                            flexShrink: 0,
                            ...(!isAvailable && {
                                backgroundColor: 'white',
                            }),
                            '&:hover': {
                                cursor: 'zoom-in',
                            },
                        }}
                    >
                        <CatalogItemImage
                            src={thumbnailUrl}
                            height="100px"
                            width="100px"
                        />
                        {itemLabel && (
                            <Typography
                                data-testclass="item-inventory-label"
                                variant="caption"
                                color="black"
                                align="center"
                                sx={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    backgroundColor:
                                        'rgba(255, 255, 255, 0.85)',
                                    paddingTop: '8px',
                                    paddingBottom: '8px',
                                    fontSize: '1.2rem',
                                }}
                            >
                                {itemLabel}
                            </Typography>
                        )}
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexGrow: 1,
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            minHeight: '100px',
                            minWidth: 0,
                            gap: '12px',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                minWidth: 0,
                            }}
                        >
                            <ProductRowTitleBlock
                                title={title}
                                brand={brand}
                                uom={uom}
                                qtyPerUom={qtyPerUom}
                                isAddedToCatalog={isAddedToCatalog}
                                interactive
                                onClickTitle={onClickTitle}
                                titleDataTestClass="item-title"
                                brandUomDataTestClass="item-brand-uom"
                            />
                        </Box>

                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'flex-end',
                                columnGap: '12px',
                                rowGap: '8px',
                                minWidth: 0,
                            }}
                        >
                            <Box sx={{ display: 'contents' }}>
                                {supplierSlot}
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexGrow: 1,
                                    flexBasis: 'auto',
                                    alignItems: 'center',
                                    justifyContent: 'flex-end',
                                    minWidth: 'max-content',
                                }}
                            >
                                {action}
                            </Box>
                        </Box>
                    </Box>
                </Box>
                <Box
                    sx={{
                        marginLeft: leadingAction ? '148px' : '120px',
                        paddingLeft: '8px',
                    }}
                >
                    {priceSlot}
                </Box>
            </Box>
        );
    },
);
